/**
 * BiOui — lot 1 : bouton « Ajouter » de la suggestion qui comble l'écart vers la livraison offerte (tiroir et page
 * panier). Ajout AJAX puis re-rendu des sections panier par l'événement standard (comme le formulaire produit d'Horizon).
 */
import { CartLinesUpdateEvent } from '@shopify/events';

async function majPanier(url, corps) {
  const sections = [...document.querySelectorAll('cart-items-component')]
    .map((c) => c instanceof HTMLElement && c.dataset.sectionId)
    .filter(Boolean);
  const promesse = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: corps.items ? 'add' : 'update',
      context: 'cart',
      lines: corps.items ? corps.items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })) : [],
      promise: promesse.promise,
    })
  );
  try {
    const rep = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...corps, sections: sections.join(','), sections_url: location.pathname }),
    }).then((r) => r.json());
    const panier = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    promesse.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(panier),
      detail: { items: panier.items, source: 'ai-franco', sections: rep.sections, didError: Boolean(rep.status) },
    });
    return !rep.status;
  } catch (e) {
    promesse.reject(e);
    return false;
  }
}

class AiFrancoAjout extends HTMLElement {
  connectedCallback() {
    this.querySelector('button')?.addEventListener('click', async (e) => {
      const bouton = /** @type {HTMLButtonElement} */ (e.currentTarget);
      bouton.disabled = true;
      bouton.textContent = 'Ajout…';
      const ok =
        this.dataset.type === 'ligne'
          ? await majPanier('/cart/change.js', { line: Number(this.dataset.ligne), quantity: Number(this.dataset.quantite) })
          : await majPanier('/cart/add.js', { items: [{ id: Number(this.dataset.variante), quantity: 1 }] });
      if (!ok && bouton.isConnected) {
        bouton.disabled = false;
        bouton.textContent = 'Ajouter';
      }
    });
  }
}

if (!customElements.get('ai-franco-ajout')) customElements.define('ai-franco-ajout', AiFrancoAjout);

/**
 * 18/10 (décision Simon) : ligne « raclette » sous les articles (snippet ai-panier-raclette) et cartes de la rangée
 * « Souvent ajoutés » (snippet ai-souvent-ajoutes). Un clic sur « Ajouter » ajoute la variante `data-variante`
 * (quantité 1) et re-rend le panier : la ligne ou la carte disparaît, le produit apparaît dans les articles.
 * Le bouton disparaissant avec la ligne, le focus est rendu au titre de l'article ajouté (sinon il tomberait sur la page).
 */
class AiAjoutPanier extends HTMLElement {
  connectedCallback() {
    // Rangée « Souvent ajoutés » (18/10) : produit à plusieurs variantes (couleur du Stop-Pub rond) : la liste native
    // choisit la variante ajoutée ; vignette, prix et nom accessible du bouton suivent.
    this.querySelector('select[data-ai-choix]')?.addEventListener('change', (e) => {
      const choix = /** @type {HTMLSelectElement} */ (e.currentTarget);
      const option = choix.selectedOptions[0];
      if (!option) return;
      this.dataset.variante = option.value;
      const img = this.querySelector('img[data-ai-img]');
      if (img instanceof HTMLImageElement && option.dataset.img) img.src = option.dataset.img;
      const prix = this.querySelector('[data-ai-prix]');
      if (prix && option.dataset.prix) prix.textContent = option.dataset.prix;
      const bouton = this.querySelector('button');
      if (bouton && this.dataset.titre)
        bouton.setAttribute('aria-label', `Ajouter au panier : ${this.dataset.titre} (${option.textContent.trim()}), ${option.dataset.prix || ''}`);
    });
    this.querySelector('button')?.addEventListener('click', async (e) => {
      const bouton = /** @type {HTMLButtonElement} */ (e.currentTarget);
      if (bouton.disabled) return;
      const variante = Number(this.dataset.variante);
      const conteneur = this.closest('cart-items-component') || document;
      bouton.disabled = true;
      bouton.textContent = 'Ajout…';
      const ok = await majPanier('/cart/add.js', { items: [{ id: variante, quantity: 1 }] });
      if (!ok) {
        if (bouton.isConnected) {
          bouton.disabled = false;
          bouton.textContent = 'Ajouter';
        }
        return;
      }
      for (let i = 0; i < 20; i++) {
        await new Promise((r) => setTimeout(r, 50));
        const titre = (conteneur.isConnected ? conteneur : document).querySelector(`[data-key^="${variante}:"] .cart-items__title`);
        if (titre instanceof HTMLElement) {
          if (!document.activeElement || document.activeElement === document.body || !document.activeElement.isConnected) titre.focus();
          break;
        }
      }
    });
  }
}

if (!customElements.get('ai-ajout-panier')) customElements.define('ai-ajout-panier', AiAjoutPanier);

/**
 * R4 : « Entrée » dans un champ quantité du panier ou du tiroir soumettait le formulaire du panier, donc partait au
 * paiement sans mise à jour visible. On valide la saisie (blur → POST /cart/change d'Horizon) au lieu de soumettre.
 */
if (!window.__aiEntreePanier) {
  window.__aiEntreePanier = true;
  document.addEventListener(
    'keydown',
    (e) => {
      const cible = e.target;
      if (e.key === 'Enter' && cible instanceof HTMLInputElement && cible.closest('.cart-items__quantity, cart-quantity-selector-component')) {
        e.preventDefault();
        cible.blur();
      }
    },
    true
  );
}
